// Adapté de ScanGabon/apps/capture/src/recadrage/geometrie.ts, lecture du 24 octobre 2026.
// Reprise locale pour CollectivitésConnect ; aucun service distant ni donnée source.
// Geometrie du recadrage de la feuille : quadrilatere des quatre coins, ordre des coins,
// homographie (transformation de perspective) et dimensions de la page redressee.
// Fonctions pures, deterministes et testables sans navigateur. Aucune transformation generative :
// le redressement ne fait que deplacer des pixels existants selon une transformation geometrique.
import type { Coins, Point, Recadrage } from './types';

// Matrice 3x3 ligne par ligne (9 valeurs), h22 normalise a 1.
export type Homographie = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

// Coins de l'image entiere (aucun recadrage), en coordonnees normalisees.
export const PAGE_ENTIERE: Coins = [
  { x: 0, y: 0 },
  { x: 1, y: 0 },
  { x: 1, y: 1 },
  { x: 0, y: 1 },
];

export const NOMS_COINS = ['haut gauche', 'haut droit', 'bas droit', 'bas gauche'] as const;

const EPSILON = 1e-9;

function produitVectoriel(o: Point, a: Point, b: Point): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

// Ordonne quatre points en haut gauche, haut droit, bas droit, bas gauche (sens horaire a l'ecran,
// l'axe y descendant). Le premier coin est celui dont x + y est le plus petit.
export function ordonnerCoins(points: readonly Point[]): Coins {
  if (points.length !== 4) throw new Error('Quatre coins sont attendus');
  const cx = points.reduce((s, p) => s + p.x, 0) / 4;
  const cy = points.reduce((s, p) => s + p.y, 0) / 4;
  const tries = [...points].sort(
    (a, b) => Math.atan2(a.y - cy, a.x - cx) - Math.atan2(b.y - cy, b.x - cx),
  );
  let debut = 0;
  for (let i = 1; i < 4; i++) {
    const p = tries[i] as Point;
    const d = tries[debut] as Point;
    if (p.x + p.y < d.x + d.y - EPSILON) debut = i;
  }
  const r = [0, 1, 2, 3].map((i) => tries[(debut + i) % 4] as Point);
  return [r[0], r[1], r[2], r[3]] as unknown as Coins;
}

// Aire du quadrilatere (formule du lacet), toujours positive.
export function aireQuadrilatere(c: readonly Point[]): number {
  let s = 0;
  for (let i = 0; i < c.length; i++) {
    const a = c[i] as Point;
    const b = c[(i + 1) % c.length] as Point;
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) / 2;
}

// Vrai si le quadrilatere est strictement convexe (pas de coin aligne ni de cote croise).
export function estConvexe(c: readonly Point[]): boolean {
  if (c.length !== 4) return false;
  let signe = 0;
  for (let i = 0; i < 4; i++) {
    const v = produitVectoriel(c[i] as Point, c[(i + 1) % 4] as Point, c[(i + 2) % 4] as Point);
    if (Math.abs(v) < EPSILON) return false;
    const s = Math.sign(v);
    if (signe === 0) signe = s;
    else if (s !== signe) return false;
  }
  // Un pentagramme croise a des produits de meme signe mais une somme d'angles de 720 degres :
  // on verifie aussi que les diagonales se coupent.
  return segmentsSeCoupent(c[0] as Point, c[2] as Point, c[1] as Point, c[3] as Point);
}

function segmentsSeCoupent(a: Point, b: Point, c: Point, d: Point): boolean {
  const d1 = produitVectoriel(a, b, c);
  const d2 = produitVectoriel(a, b, d);
  const d3 = produitVectoriel(c, d, a);
  const d4 = produitVectoriel(c, d, b);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

// Angles interieurs en degres, dans l'ordre des coins.
export function anglesInterieurs(c: readonly Point[]): number[] {
  return c.map((p, i) => {
    const prec = c[(i + 3) % 4] as Point;
    const suiv = c[(i + 1) % 4] as Point;
    const ux = prec.x - p.x;
    const uy = prec.y - p.y;
    const vx = suiv.x - p.x;
    const vy = suiv.y - p.y;
    const n = Math.hypot(ux, uy) * Math.hypot(vx, vy);
    if (n < EPSILON) return 0;
    const cos = Math.max(-1, Math.min(1, (ux * vx + uy * vy) / n));
    return (Math.acos(cos) * 180) / Math.PI;
  });
}

export interface CriteresQuadrilatere {
  // Aire minimale en fraction de l'aire de reference (1 pour des coordonnees normalisees).
  aireMin: number;
  angleMin: number;
  angleMax: number;
}

export const CRITERES_SAISIE: CriteresQuadrilatere = { aireMin: 0.01, angleMin: 15, angleMax: 165 };

// Quadrilatere utilisable pour un redressement : convexe, assez grand, sans angle aberrant.
export function quadrilatereValide(
  c: readonly Point[],
  criteres: CriteresQuadrilatere = CRITERES_SAISIE,
  aireReference = 1,
): boolean {
  if (c.length !== 4 || !c.every((p) => Number.isFinite(p.x) && Number.isFinite(p.y))) {
    return false;
  }
  if (!estConvexe(c)) return false;
  if (aireQuadrilatere(c) < criteres.aireMin * aireReference) return false;
  return anglesInterieurs(c).every((a) => a >= criteres.angleMin && a <= criteres.angleMax);
}

// Resolution d'un systeme lineaire n x n par elimination de Gauss avec pivot partiel.
// Renvoie null si le systeme est singulier.
function resoudre(a: number[][], b: number[]): number[] | null {
  const n = b.length;
  const m = a.map((ligne, i) => [...ligne, b[i] as number]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let l = col + 1; l < n; l++) {
      if (
        Math.abs((m[l] as number[])[col] as number) >
        Math.abs((m[pivot] as number[])[col] as number)
      ) {
        pivot = l;
      }
    }
    const lp = m[pivot] as number[];
    if (Math.abs(lp[col] as number) < 1e-12) return null;
    [m[col], m[pivot]] = [lp, m[col] as number[]];
    const lc = m[col] as number[];
    for (let l = 0; l < n; l++) {
      if (l === col) continue;
      const ll = m[l] as number[];
      const f = (ll[col] as number) / (lc[col] as number);
      if (f === 0) continue;
      for (let k = col; k <= n; k++) ll[k] = (ll[k] as number) - f * (lc[k] as number);
    }
  }
  return m.map((ligne, i) => (ligne[n] as number) / (ligne[i] as number));
}

function troisAlignes(p: readonly Point[]): boolean {
  for (let i = 0; i < 4; i++) {
    const a = p[i] as Point;
    const b = p[(i + 1) % 4] as Point;
    const c = p[(i + 2) % 4] as Point;
    const echelle = Math.max(distance(a, b), distance(b, c), EPSILON);
    if (Math.abs(produitVectoriel(a, b, c)) < 1e-9 * echelle * echelle) return true;
  }
  return false;
}

// Homographie H telle que H(source[i]) = cible[i] pour les quatre correspondances.
// Renvoie null si l'un des deux quadrilateres est degenere (trois points alignes).
export function homographie(source: readonly Point[], cible: readonly Point[]): Homographie | null {
  if (source.length !== 4 || cible.length !== 4) return null;
  if (troisAlignes(source) || troisAlignes(cible)) return null;
  const a: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = source[i] as Point;
    const { x: u, y: v } = cible[i] as Point;
    a.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
    b.push(u);
    a.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
    b.push(v);
  }
  const h = resoudre(a, b);
  if (!h || !h.every(Number.isFinite)) return null;
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1] as unknown as Homographie;
}

export function appliquerHomographie(h: Homographie, p: Point): Point {
  const w = h[6] * p.x + h[7] * p.y + h[8];
  return { x: (h[0] * p.x + h[1] * p.y + h[2]) / w, y: (h[3] * p.x + h[4] * p.y + h[5]) / w };
}

export function versPixels(coins: Coins, largeur: number, hauteur: number): Coins {
  return coins.map((p) => ({ x: p.x * largeur, y: p.y * hauteur })) as unknown as Coins;
}

// Dimensions de la page redressee estimees depuis les cotes du quadrilatere (en pixels) : la plus
// grande des deux longueurs opposees, pour ne pas perdre de definition. Le resultat est borne par
// une cote maximale et par l'aire de l'image source (le poids du PDF ne grossit pas).
export function dimensionsRedressees(
  coinsPx: Coins,
  limites: { coteMax?: number; aireMax?: number } = {},
): { largeur: number; hauteur: number } {
  const [hg, hd, bd, bg] = coinsPx;
  let largeur = Math.max(distance(hg, hd), distance(bg, bd));
  let hauteur = Math.max(distance(hg, bg), distance(hd, bd));
  let echelle = 1;
  if (limites.coteMax) echelle = Math.min(echelle, limites.coteMax / Math.max(largeur, hauteur));
  if (limites.aireMax)
    echelle = Math.min(echelle, Math.sqrt(limites.aireMax / (largeur * hauteur)));
  largeur = Math.max(1, Math.round(largeur * echelle));
  hauteur = Math.max(1, Math.round(hauteur * echelle));
  return { largeur, hauteur };
}

export function borner(v: number): number {
  return Math.min(1, Math.max(0, v));
}

export function bornerCoins(c: Coins): Coins {
  return c.map((p) => ({ x: borner(p.x), y: borner(p.y) })) as unknown as Coins;
}

export function estPageEntiere(c: Coins, tolerance = 1e-6): boolean {
  return c.every(
    (p, i) =>
      Math.abs(p.x - (PAGE_ENTIERE[i] as Point).x) <= tolerance &&
      Math.abs(p.y - (PAGE_ENTIERE[i] as Point).y) <= tolerance,
  );
}

// Rectangle aligne sur les axes (recadrage simple, sans perspective) : renvoie ses bornes.
export function rectangleAligne(
  c: Coins,
  tolerance = 1e-6,
): { gauche: number; haut: number; droite: number; bas: number } | null {
  const [hg, hd, bd, bg] = c;
  const ok =
    Math.abs(hg.y - hd.y) <= tolerance &&
    Math.abs(bg.y - bd.y) <= tolerance &&
    Math.abs(hg.x - bg.x) <= tolerance &&
    Math.abs(hd.x - bd.x) <= tolerance &&
    hd.x > hg.x &&
    bg.y > hg.y;
  return ok ? { gauche: hg.x, haut: hg.y, droite: hd.x, bas: bg.y } : null;
}

// Migration de l'ancien recadrage rectangulaire (marges en pourcentage) vers quatre coins.
export function coinsDepuisRecadrage(r: Recadrage): Coins {
  const g = borner((r.gauche || 0) / 100);
  const d = borner(1 - (r.droite || 0) / 100);
  const h = borner((r.haut || 0) / 100);
  const b = borner(1 - (r.bas || 0) / 100);
  return [
    { x: g, y: h },
    { x: d, y: h },
    { x: d, y: b },
    { x: g, y: b },
  ];
}

// Coins valides pour un stockage local : quatre points finis dans [0, 1].
export function coinsLisibles(v: unknown): v is Coins {
  return (
    Array.isArray(v) &&
    v.length === 4 &&
    v.every(
      (p: unknown) =>
        typeof p === 'object' &&
        p !== null &&
        typeof (p as Point).x === 'number' &&
        typeof (p as Point).y === 'number' &&
        Number.isFinite((p as Point).x) &&
        Number.isFinite((p as Point).y) &&
        (p as Point).x >= 0 &&
        (p as Point).x <= 1 &&
        (p as Point).y >= 0 &&
        (p as Point).y <= 1,
    )
  );
}

// Polygone CSS (clip-path) d'un quadrilatere normalise.
export function polygoneCss(c: Coins): string {
  return `polygon(${c.map((p) => `${(p.x * 100).toFixed(2)}% ${(p.y * 100).toFixed(2)}%`).join(', ')})`;
}
